import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lock } from "lucide-react";
import { TID } from "@/constants/testIds";
import { NEMA_LOGO } from "@/assets/logo";
import { RotatingGreeting } from "@/components/site/Greeting";

/*
  UVOtv-onboarding-inspired hero:
   - Compact centered modal panel
   - Nema logo + "Choose your package"
   - Desi TV tile (branded logo) + Arabic TV tile (flag cluster · coming soon)
   - Hover a tile → soft palette-matched glow fades in behind the panel
   - "More packages · coming soon" footer
   - "Start Watching" CTA (activates when a tile is selected)
   - Mobile-tuned at 375px (iPhone SE) — tighter padding + smaller flags
*/

const DESI_LOGO = "/logos/desi-tv.png";

const FLAG = "https://flagcdn.com/w160";

const arabicFlags = [
  { code: "sa", name: "Saudi Arabia" },
  { code: "ae", name: "United Arab Emirates" },
  { code: "eg", name: "Egypt" },
  { code: "lb", name: "Lebanon" },
];

const FlagCluster = ({ flags, dim = false }) => (
  <div className="flex justify-center -space-x-2.5 sm:-space-x-3">
    {flags.map((f, i) => (
      <img
        key={f.code}
        src={`${FLAG}/${f.code}.png`}
        alt={f.name}
        loading="lazy"
        className={`w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full object-cover ring-2 ring-[#0F0A19] shadow-lg ${dim ? "opacity-75" : ""}`}
        style={{ zIndex: flags.length - i }}
      />
    ))}
  </div>
);

const Tile = ({ selected, onHover, onLeave, onClick, testId, children, ringColor }) => (
  <button
    type="button"
    onClick={onClick}
    onMouseEnter={onHover}
    onMouseLeave={onLeave}
    onFocus={onHover}
    onBlur={onLeave}
    data-testid={testId}
    data-selected={selected ? "true" : "false"}
    className={`relative rounded-2xl p-3.5 sm:p-5 md:p-6 text-left transition-all overflow-hidden group ${
      selected
        ? "border border-transparent bg-white/[0.06]"
        : "border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20"
    }`}
    style={
      selected
        ? { boxShadow: `0 0 0 2px ${ringColor}, 0 0 40px -8px ${ringColor}66` }
        : undefined
    }
  >
    {children}
  </button>
);

/*
  Sliding hero banner backdrop — cross-fades between the two attached
  cinematic banners (Desi Taj Mahal · Arabic skyline) with a slow Ken-Burns
  zoom. Sits behind the "Choose your package" modal.
*/
const SLIDES = [
  { src: "/hero-slides/desi.png",      alt: "Desi TV — Taj Mahal at sunset",       tint: "desi",      label: "Desi TV" },
  { src: "/hero-slides/himalayan.jpg", alt: "Cinematic mountain dusk",             tint: "himalayan", label: "Himalayan Dusk" },
  { src: "/hero-slides/arabic.png",    alt: "Arabic TV — city skyline at sunset",  tint: "arabic",    label: "Arabic TV" },
];

const SlidingBackdrop = ({ idx }) => (
    <div
      aria-hidden="true"
      data-testid="hero-sliding-backdrop"
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
    >
      <AnimatePresence>
        {SLIDES.map((s, i) =>
          i === idx ? (
            <motion.div
              key={s.src}
              data-testid={`hero-slide-${s.tint}`}
              initial={{ opacity: 0, scale: 1.08 }}
              animate={{ opacity: 1, scale: 1.14 }}
              exit={{ opacity: 0, scale: 1.18 }}
              transition={{ opacity: { duration: 1.8 }, scale: { duration: 8, ease: "linear" } }}
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${s.src})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          ) : null,
        )}
      </AnimatePresence>
      {/* Layered darkening + brand vignette so the modal reads clearly */}
      <div className="absolute inset-0 bg-[#05030A]/20" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(5,3,10,0.05)_0%,rgba(5,3,10,0.35)_55%,rgba(5,3,10,0.78)_100%)]" />
    </div>
);

const SlideDots = ({ idx, onPick }) => (
  <div
    data-testid="hero-slide-dots"
    className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[3] flex items-center gap-2"
  >
    {SLIDES.map((s, i) => (
      <button
        key={s.src}
        type="button"
        onClick={() => onPick(i)}
        aria-label={`Show slide: ${s.label}`}
        data-testid={`hero-slide-dot-${s.tint}`}
        data-active={i === idx ? "true" : "false"}
        className={`h-1.5 rounded-full transition-all duration-500 ${
          i === idx
            ? "w-8 bg-white shadow-[0_0_12px_rgba(255,255,255,0.6)]"
            : "w-2 bg-white/40 hover:bg-white/70"
        }`}
      />
    ))}
  </div>
);

const Hero = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null); // "desi" | "arabic" | null
  const [hovered, setHovered] = useState(null);   // "desi" | "arabic" | null
  const [slideIdx, setSlideIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlideIdx((i) => (i + 1) % SLIDES.length), 7000);
    return () => clearInterval(t);
  }, []);

  const active = hovered || selected; // hover overrides for the ambient glow

  const startWatching = () => {
    if (selected === "desi") {
      navigate("/packages/desi-tv");
    } else if (selected === "arabic") {
      navigate("/packages/arabic-tv");
    }
  };

  const canStart = selected === "desi" || selected === "arabic";

  return (
    <section
      id="top"
      data-testid={TID.hero.root}
      className="relative min-h-[100svh] pt-24 pb-14 md:pt-32 flex items-center justify-center overflow-hidden"
    >
      {/* Base ambient background */}
      <div className="glow-blob bg-[#6B21D9]/35 w-[560px] h-[560px] -top-40 -left-40" />
      <div className="glow-blob bg-[#22D3EE]/25 w-[460px] h-[460px] bottom-0 right-0" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(107,33,217,0.24),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(34,211,238,0.16),transparent_55%)] -z-10" />

      {/* Sliding cinematic hero banner backdrop */}
      <SlidingBackdrop idx={slideIdx} />
      <SlideDots idx={slideIdx} onPick={setSlideIdx} />

      {/* Palette-matched glow that fades in behind the panel on hover/select */}
      <AnimatePresence>
        {active === "desi" && (
          <motion.div
            key="glow-desi"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-glow-desi"
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-[1]"
            style={{
              background:
                "radial-gradient(closest-side at 50% 50%, rgba(255,138,0,0.32) 0%, rgba(244,63,94,0.20) 35%, transparent 70%)",
            }}
          />
        )}
        {active === "arabic" && (
          <motion.div
            key="glow-arabic"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-glow-arabic"
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-[1]"
            style={{
              background:
                "radial-gradient(closest-side at 50% 50%, rgba(107,33,217,0.35) 0%, rgba(34,211,238,0.20) 40%, transparent 72%)",
            }}
          />
        )}
      </AnimatePresence>

      {/* Modal-style panel */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.55 }}
        data-testid="hero-panel"
        className="relative z-[2] w-[calc(100vw-1.5rem)] max-w-md md:max-w-lg mx-3 sm:mx-4 md:mx-auto rounded-3xl bg-[#0B0714]/95 backdrop-blur-xl border border-white/10 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)] px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-10"
      >
        {/* Logo */}
        <div className="flex justify-center mb-5 sm:mb-6">
          <img src={NEMA_LOGO} alt="Nema TV" className="h-7 sm:h-8 md:h-9 w-auto" />
        </div>

        {/* Title */}
        <h1 className="font-display text-xl sm:text-2xl md:text-3xl font-extrabold text-white text-center leading-tight">
          Choose your package
        </h1>

        {/* Rotating tri-language greeting */}
        <div className="mt-3 sm:mt-4">
          <RotatingGreeting testId="hero-greeting" small />
        </div>

        {/* Tiles */}
        <div className="mt-5 sm:mt-7 grid grid-cols-2 gap-2.5 sm:gap-3 md:gap-4">
          {/* Desi */}
          <Tile
            selected={selected === "desi"}
            onHover={() => setHovered("desi")}
            onLeave={() => setHovered(null)}
            onClick={() => setSelected("desi")}
            testId="hero-tile-desi"
            ringColor="rgba(255,138,0,0.9)"
          >
            <div className="flex flex-col items-center py-1.5 sm:py-2">
              <div className="h-11 sm:h-12 md:h-14 flex items-center justify-center">
                <img
                  src={DESI_LOGO}
                  alt="Desi TV"
                  data-testid="hero-tile-desi-logo"
                  className="h-9 sm:h-10 md:h-12 w-auto object-contain drop-shadow-[0_2px_10px_rgba(255,138,0,0.35)]"
                />
              </div>
              <div className="mt-3 sm:mt-4 text-center">
                <div className="font-display text-sm sm:text-base md:text-lg font-bold text-white leading-tight">
                  Desi TV Package
                </div>
                <div className="text-[9px] sm:text-[10px] md:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-white/50 mt-1 leading-snug">
                  India · Bangladesh
                  <br className="sm:hidden" />
                  {" · "}Pakistan · Nepal
                </div>
              </div>
              <span className="mt-2.5 sm:mt-3 inline-flex items-center gap-1.5 rounded-full bg-red-500/12 border border-red-400/40 px-2.5 py-0.5 text-[9px] uppercase tracking-[0.2em] text-red-300">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                Live now
              </span>
            </div>
          </Tile>

          {/* Arabic */}
          <Tile
            selected={selected === "arabic"}
            onHover={() => setHovered("arabic")}
            onLeave={() => setHovered(null)}
            onClick={() => setSelected("arabic")}
            testId="hero-tile-arabic"
            ringColor="rgba(107,33,217,0.9)"
          >
            <div className="flex flex-col items-center py-1.5 sm:py-2">
              <div className="h-11 sm:h-12 md:h-14 flex items-center justify-center">
                <FlagCluster flags={arabicFlags} dim />
              </div>
              <div className="mt-3 sm:mt-4 text-center">
                <div className="font-display text-sm sm:text-base md:text-lg font-bold text-white/90 leading-tight">
                  Arabic TV Package
                </div>
                <div className="text-[9px] sm:text-[10px] md:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-white/50 mt-1">
                  Arab world
                </div>
              </div>
              <span className="mt-2.5 sm:mt-3 inline-flex items-center gap-1 rounded-full bg-[#22D3EE]/10 border border-[#22D3EE]/40 px-2.5 py-0.5 text-[9px] uppercase tracking-[0.2em] text-[#67e8f9]">
                <Lock className="w-2.5 h-2.5" />
                Coming soon
              </span>
            </div>
          </Tile>
        </div>

        {/* More packages */}
        <div
          data-testid="hero-more-packages"
          className="mt-3 sm:mt-4 rounded-2xl border border-dashed border-white/10 bg-white/[0.02] px-4 sm:px-5 py-3 sm:py-4 flex items-center gap-3 sm:gap-4"
        >
          <svg viewBox="0 0 40 40" className="w-8 h-8 sm:w-10 sm:h-10 shrink-0 opacity-70" aria-hidden="true">
            <defs>
              <linearGradient id="mp" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0" stopColor="#6B21D9" />
                <stop offset="1" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path
              d="M4 22 C 10 12, 18 12, 22 20 S 34 30, 36 20"
              fill="none"
              stroke="url(#mp)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <circle cx="10" cy="17" r="1.7" fill="#22D3EE" />
            <circle cx="20" cy="18" r="1.7" fill="#F43F5E" />
            <circle cx="30" cy="22" r="1.7" fill="#FFB800" />
          </svg>
          <div className="flex-1">
            <div className="font-display text-sm md:text-base font-semibold text-white leading-tight">
              More packages
            </div>
            <div className="text-xs text-white/50 mt-0.5">coming soon</div>
          </div>
        </div>

        {/* CTA */}
        <button
          type="button"
          onClick={startWatching}
          disabled={!canStart}
          data-testid="hero-start-watching"
          className={`mt-5 sm:mt-6 w-full rounded-full py-3 sm:py-3.5 text-sm md:text-base font-bold transition-all ${
            canStart
              ? selected === "desi"
                ? "text-[#0a0503] hover:scale-[1.01]"
                : "text-white bg-gradient-to-r from-[#6B21D9] to-[#22D3EE] hover:shadow-[0_0_30px_rgba(34,211,238,0.45)]"
              : "text-white/60 bg-white/[0.06] border border-white/10 cursor-not-allowed"
          }`}
          style={
            canStart && selected === "desi"
              ? {
                  background: "linear-gradient(90deg,#FFB800 0%,#FF7A1A 50%,#F43F5E 100%)",
                  boxShadow: "0 0 30px rgba(255,138,0,0.45)",
                }
              : undefined
          }
        >
          {selected === "arabic" ? "Learn more" : "Start Watching"}
        </button>

        <div className="mt-3 text-center text-[9px] sm:text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.28em] text-white/35">
          Roku · Fire TV · Android · iOS · Web
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
